// utils/avatarGenerator.ts

/**
 * Genera un avatar con las iniciales del usuario
 * @param nombre - Nombre del usuario
 * @param apellido - Apellido del usuario
 * @param size - Tamaño del avatar (default: 200)
 * @returns URL del avatar generado
 */
export const generateInitialsAvatar = (
  nombre: string, 
  apellido: string, 
  size: number = 200
): string => {
  // Obtener iniciales
  const iniciales = getInitials(nombre, apellido);
  
  // Generar color basado en las iniciales (consistente para el mismo usuario)
  const color = getColorFromInitials(iniciales);
  
  // Crear canvas para generar la imagen
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  
  // Fondo con gradiente
  const gradient = ctx.createLinearGradient(0, 0, size, size);
  gradient.addColorStop(0, color.primary);
  gradient.addColorStop(1, color.secondary);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  
  // Texto con iniciales
  ctx.fillStyle = '#FFFFFF';
  ctx.font = `bold ${size * 0.4}px Inter, -apple-system, BlinkMacSystemFont, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(iniciales, size / 2, size / 2);
  
  // Convertir a base64
  return canvas.toDataURL('image/png');
};

/**
 * Genera una imagen de portada por defecto con gradiente
 * @param nombre - Nombre del usuario
 * @param apellido - Apellido del usuario  
 * @returns URL de la imagen de portada
 */
export const generateDefaultCover = (
  nombre: string,
  apellido: string
): string => {
  const iniciales = getInitials(nombre, apellido);
  const color = getColorFromInitials(iniciales);
  
  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 400;
  
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  
  // Gradiente más suave para la portada
  const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
  gradient.addColorStop(0, color.primary);
  gradient.addColorStop(0.5, adjustBrightness(color.primary, 20));
  gradient.addColorStop(1, color.secondary);
  
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Agregar patrón de círculos decorativos
  ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
  for (let i = 0; i < 20; i++) {
    const x = Math.random() * canvas.width;
    const y = Math.random() * canvas.height;
    const radius = Math.random() * 100 + 50;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }
  
  return canvas.toDataURL('image/png');
};

/**
 * Obtiene las iniciales de un nombre y apellido
 */
const getInitials = (nombre: string, apellido: string): string => {
  const n = nombre?.trim().charAt(0).toUpperCase() || '';
  const a = apellido?.trim().charAt(0).toUpperCase() || '';
  return n + a || '??';
};

/**
 * Genera un color consistente basado en las iniciales
 */
const getColorFromInitials = (iniciales: string): { primary: string; secondary: string } => {
  const colors = [
    { primary: '#3B82F6', secondary: '#1E40AF' }, // Azul
    { primary: '#8B5CF6', secondary: '#6D28D9' }, // Púrpura
    { primary: '#EC4899', secondary: '#BE185D' }, // Rosa
    { primary: '#10B981', secondary: '#047857' }, // Verde
    { primary: '#F59E0B', secondary: '#D97706' }, // Naranja
    { primary: '#EF4444', secondary: '#B91C1C' }, // Rojo
    { primary: '#06B6D4', secondary: '#0E7490' }, // Cyan
    { primary: '#6366F1', secondary: '#4338CA' }, // Indigo
  ];
  
  // Hash simple de las iniciales para seleccionar color consistente
  const hash = iniciales.charCodeAt(0) + (iniciales.charCodeAt(1) || 0);
  const index = hash % colors.length;
  
  return colors[index];
};

/**
 * Ajusta el brillo de un color hexadecimal
 */
const adjustBrightness = (hex: string, percent: number): string => {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  
  return '#' + (
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  ).toString(16).slice(1);
};

/**
 * Verifica si una URL de imagen está vacía o es inválida
 */
export const isEmptyOrInvalidImage = (url: string | null | undefined): boolean => {
  if (!url) return true;
  if (url.trim() === '') return true;
  if (url === 'null' || url === 'undefined') return true;
  return false;
};